/**
 * useDigestHighlights Hook (local-first)
 *
 * Local-first read surface for digest highlights (design 2026-07-06 §5C).
 * Server list cached in `meta` via useLocalFirstQuery (canonical flattened
 * form: `{ highlights: [...] }` for both scopes); offline-created neg-id rows
 * overlaid from db.highlights. Establishes the §5C settlement invariant — a
 * logical highlight is never rendered twice and never absent from a surface
 * that could render it, regardless of connectivity — via three mechanisms:
 *
 *   1. Display-level dedupe: an optimistic (neg-id) row whose digest_text
 *      matches a cached server row's exact_text is suppressed, so a
 *      reconnection revalidation that lands the confirmed row while the neg-id
 *      row still exists never renders both.
 *   2. Bounded settlement mask: when a neg-id row disappears because its
 *      mutation SETTLED (ok, not discarded), a synthesized confirmed row is
 *      retained in React state until the server list contains its text — this
 *      bridges the async gap between the neg-id delete and the post-drain
 *      refresh (and the content-hash-identical refresh where serverPayload
 *      never changes). Discarded (failed) mutations are NOT retained (§6).
 *   3. Settlement write-through: on settlement, the synthesized row is upserted
 *      into the Dexie `meta` cache for BOTH the 'all' and the topic scope keys
 *      that already exist, so navigating away and back (offline, or within
 *      fresh-TTL) still renders the highlight — later real hydration supersedes
 *      it via mechanism 1's dedupe.
 *
 * Scopes:
 *   { scope: 'all' }                      — DailyBriefing (all topics, flattened)
 *   { scope: 'topic', topicName: label }  — TopicDetail (one topic)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { liveQuery } from 'dexie';
import { useLocalFirstQuery } from './useLocalFirstQuery';
import { HIGHLIGHTS_TTL_MS } from '../db/freshness';
import { db } from '../db';
import { contentHash } from '../db/contentHash';
import { MUTATION_STATUS } from '../services/mutationLog';
import { getAllDigestHighlights, getDigestHighlightsByTopic } from '../services/highlights';

const ALL_KEY = 'digest_highlights:all';
// Reason: topic labels are arbitrary LLM strings, so a "no topic yet" sentinel
// MUST NOT be constructible as a real topic key. Real keys always carry the
// `topic:` segment; the sentinel deliberately omits it, so even a topic literally
// labeled "__none__" (→ 'digest_highlights:topic:__none__') can't collide.
const NONE_KEY = 'digest_highlights:__none__';
const topicKeyFor = (name) => `digest_highlights:topic:${name}`;

// Neg-id digest-highlight rows written by applyDigestHighlightCreate.
function scopedLocalRowsQuery(scope, topicName) {
  return db.highlights
    .filter(
      (h) =>
        h.id < 0
        && h.digest_text != null
        && (scope === 'all' || h.topic_name === topicName)
    )
    .toArray();
}

// A confirmed-shaped row synthesized from a settled create's mutation payload.
// Carries BOTH digest_text and exact_text so it dedupes against the eventual
// real server row (matched by text) once hydration replaces it.
function synthesizeConfirmedRow(key, payload) {
  return {
    id: `synth-${key}`,
    article_id: payload.article_id ?? null,
    topic_name: payload.topic_name,
    digest_section: payload.digest_section ?? null,
    digest_text: payload.digest_text,
    exact_text: payload.digest_text,
    digest_version: payload.digest_version,
    created_at: new Date().toISOString(),
  };
}

const rowText = (h) => h.exact_text ?? h.digest_text;

// Idempotent upsert of a synthesized row into an EXISTING meta cache payload.
// Skips absent keys (nothing cached → nothing can vanish) and no-ops if the
// text is already present.
async function writeThroughToCache(cacheKey, synthRow) {
  const row = await db.table('meta').get(cacheKey);
  if (!row) return;
  const highlights = row.payload?.highlights || [];
  const text = rowText(synthRow);
  if (highlights.some((h) => rowText(h) === text)) return;
  const payload = { highlights: [...highlights, synthRow] };
  await db.table('meta').put({
    ...row,
    payload,
    content_hash: await contentHash(payload),
    fetched_at: Date.now(),
  });
}

export function useDigestHighlights({ scope, topicName = null }) {
  const [liveRows, setLiveRows] = useState([]);
  // Settlement mask: synthesized confirmed rows kept visible until the server
  // list catches up. Keyed by synth id so re-settlement is idempotent.
  const [settledRows, setSettledRows] = useState([]);

  const cacheKey = scope === 'all'
    ? ALL_KEY
    : (topicName ? topicKeyFor(topicName) : NONE_KEY);

  const fetchFn = useCallback(async () => {
    if (scope === 'all') {
      const response = await getAllDigestHighlights();
      // Real API (backend/schemas/highlight.py DigestHighlightsResponse):
      // `topics` is a LIST of { topic_name, highlights }. Flatten to the
      // canonical `{ highlights: [...] }` form both scopes share, and sort by
      // id so the content hash is stable across the server's recency reordering.
      const groups = Array.isArray(response.topics)
        ? response.topics
        : Object.values(response.topics || {});
      const flattened = [];
      groups.forEach((g) => {
        if (g?.highlights) flattened.push(...g.highlights);
      });
      flattened.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      return { status: 200, etag: null, payload: { highlights: flattened } };
    }
    if (!topicName) {
      return { status: 200, etag: null, payload: { highlights: [] } };
    }
    const response = await getDigestHighlightsByTopic(topicName);
    const highlights = [...(response.highlights || [])].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    return { status: 200, etag: null, payload: { highlights } };
  }, [scope, topicName]);

  const { data: serverPayload, refresh } = useLocalFirstQuery({
    table: 'meta',
    key: cacheKey,
    ttl: HIGHLIGHTS_TTL_MS,
    fetch: fetchFn,
  });

  // Optimistic-write overlay: watch this scope's neg-id rows so a queued create
  // renders immediately (the pages call mutate() directly, so this hook has no
  // create callback to piggyback on).
  useEffect(() => {
    setLiveRows([]);
    const sub = liveQuery(() => scopedLocalRowsQuery(scope, topicName)).subscribe({
      next: setLiveRows,
      error: () => {},
    });
    return () => sub.unsubscribe();
  }, [scope, topicName]);

  // Settlement watcher: track this scope's live digest_highlight_create rows.
  // A key that LEAVES the log entirely is classified by the status it was LAST
  // SEEN in, which the snapshot already carries:
  //   - last seen pending/in_flight → the drain deleted it on ok → settled →
  //     synthesize + write-through both cache keys + mask the row + refresh()
  //     so the real server row converges.
  //   - last seen failed → the user discarded it from Pending Issues (the only
  //     path that deletes a failed row of this type) → NOT settled; the
  //     rollback already removed the neg-id row, so the highlight vanishes (§6).
  // A row that merely turns failed stays in the snapshot, so it is not a
  // departure at all and renders as pending until retry or discard.
  //
  // Reason: classifying from the snapshot replaces an async re-read of the log
  // row, which raced. applyBatchResult writes `failed` in one commit and
  // discardMutation deletes in another; if the discard landed while that
  // re-read was in flight the row read as absent, and a discarded highlight was
  // masked back onto the screen as a settled one.
  const prevPendingRef = useRef(new Map());
  useEffect(() => {
    prevPendingRef.current = new Map();
    const sub = liveQuery(() =>
      db.table('mutation_log')
        .where('status')
        .anyOf([MUTATION_STATUS.PENDING, MUTATION_STATUS.IN_FLIGHT, MUTATION_STATUS.FAILED])
        .toArray()
    ).subscribe({
      next: async (rows) => {
        // Reason: this callback awaits Dexie reads/writes; wrap the whole body
        // so a transient Dexie error (e.g. the db closing during teardown) is
        // swallowed rather than escaping as an unhandled rejection (the
        // subscription's `error` handler only catches the liveQuery observable,
        // not rejections thrown inside this async next callback).
        try {
          const cur = new Map();
          rows.forEach((m) => {
            if (
              m.type === 'digest_highlight_create'
              && (scope === 'all' || m.payload?.topic_name === topicName)
            ) {
              cur.set(m.key, { payload: m.payload, status: m.status });
            }
          });

          const settled = [];
          for (const [key, prev] of prevPendingRef.current) {
            if (cur.has(key)) continue;
            // Discarded from the Pending Issues inbox, not confirmed by the server.
            if (prev.status === MUTATION_STATUS.FAILED) continue;
            settled.push([key, prev.payload]);
          }
          prevPendingRef.current = cur;
          if (settled.length === 0) return;

          let settledAny = false;
          for (const [key, payload] of settled) {
            settledAny = true;
            const synth = synthesizeConfirmedRow(key, payload);
            // Write-through to BOTH scope keys that exist so either page renders
            // it after navigation (offline / fresh-TTL). Absent keys are skipped.
            await writeThroughToCache(ALL_KEY, synth);
            if (payload.topic_name) {
              await writeThroughToCache(topicKeyFor(payload.topic_name), synth);
            }
            setSettledRows((prev) =>
              prev.some((r) => r.id === synth.id) ? prev : [...prev, synth]
            );
          }
          // Pull the canonical server list so the confirmed row converges (online).
          if (settledAny) refresh();
        } catch {
          /* transient Dexie error (incl. db closed during teardown) — ignore */
        }
      },
      error: () => {},
    });
    return () => sub.unsubscribe();
  }, [scope, topicName, refresh]);

  // Server-list texts drive both the dedupe and the mask-drop below.
  const serverTexts = useMemo(() => {
    const set = new Set();
    (serverPayload?.highlights || []).forEach((h) => set.add(rowText(h)));
    return set;
  }, [serverPayload]);

  // Drop masked rows once the server list contains their text (online hydration
  // landed — including the content-hash-identical case, since this compares
  // against the server texts, not serverPayload identity).
  useEffect(() => {
    setSettledRows((prev) => {
      const next = prev.filter((r) => !serverTexts.has(rowText(r)));
      return next.length === prev.length ? prev : next;
    });
  }, [serverTexts]);

  // Scope change clears the mask entirely (retained rows belong to the previous
  // scope's surface).
  useEffect(() => {
    setSettledRows([]);
  }, [scope, topicName]);

  // Merge: server rows, then optimistic overlay minus any whose text a server
  // row already confirms (dedupe), then the settlement mask minus anything the
  // server or overlay already shows.
  const highlights = useMemo(() => {
    const server = serverPayload?.highlights || [];
    const overlay = liveRows
      .filter((h) => !serverTexts.has(rowText(h)))
      .map((r) => ({
        ...r,
        exact_text: r.exact_text ?? r.digest_text,
        created_at: r.created_at ?? (r.fetched_at ? new Date(r.fetched_at).toISOString() : null),
      }));
    const overlayTexts = new Set(overlay.map(rowText));
    const mask = settledRows.filter(
      (r) => !serverTexts.has(rowText(r)) && !overlayTexts.has(rowText(r))
    );
    if (overlay.length === 0 && mask.length === 0) return server;
    return [...server, ...overlay, ...mask];
  }, [serverPayload, serverTexts, liveRows, settledRows]);

  // Display lookup only (never reconciliation — §5C sources-of-truth contract):
  // server rows match by exact_text, optimistic rows by digest_text.
  const isDigestItemHighlighted = useCallback(
    (text) => highlights.some((h) => h.exact_text === text || h.digest_text === text),
    [highlights]
  );

  return useMemo(
    () => ({ highlights, isDigestItemHighlighted, refresh }),
    [highlights, isDigestItemHighlighted, refresh]
  );
}

export default useDigestHighlights;
